import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/onec_bases";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage } from "@/lib/api-errors";
import { useOneCBases } from "@/lib/use-onec-bases";
import { cn } from "@/lib/utils";

type FormState = {
  name: string;
  base_url: string;
  audience: string;
  config_type: api.OneCConfigType;
  metadata_key: api.OneCMetadataKey;
};

type TextField = "name" | "base_url" | "audience";

const EMPTY: FormState = {
  name: "",
  base_url: "",
  audience: "",
  config_type: "bp",
  metadata_key: "onec_bp",
};

const REQUIRED: { key: TextField; title: string }[] = [
  { key: "name", title: "Название" },
  { key: "base_url", title: "Адрес публикации" },
  { key: "audience", title: "Audience" },
];

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-70";

/** Справочник баз 1С для SSO-входа (админ). В SSO-сессии портал ходит в 1С по
 *  JWT: `aud` токена — `audience` базы, адрес — `base_url`, пользователь 1С —
 *  из metadata учётки SSO (`metadata_key`). Какая база у человека — решает его
 *  подключение (Профиль → «Подключения к 1С»), сопоставленное с базой по адресу. */
export function OneCBasesAdminPage(): React.ReactElement {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useOneCBases();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());
  // Отказ сервера — строкой у формы, не только тостом (409 «такая база уже
  // есть», 422 по адресу с /odata/): тост легко пропустить.
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: api.ONEC_BASES_QUERY_KEY });
  const onFormError = (fallback: string) => (e: unknown) => {
    const msg = extractErrorMessage(e, fallback);
    setFormError(msg);
    toast.error(msg);
  };
  const reset = () => {
    setEditingId(null);
    setForm(EMPTY);
    setMissing(new Set());
    setFormError(null);
  };

  const createMut = useMutation({
    mutationFn: api.createOneCBase,
    onSuccess: () => {
      void invalidate();
      reset();
      toast.success("База добавлена");
    },
    onError: onFormError("Ошибка добавления базы"),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: api.OneCBaseUpdate }) =>
      api.updateOneCBase(args.id, args.body),
    onSuccess: () => {
      void invalidate();
      reset();
      toast.success("База сохранена");
    },
    onError: onFormError("Ошибка сохранения базы"),
  });

  // 409 — на базу ссылаются подключения пользователей: причину сервера
  // показываем как есть (в ней — что сделать).
  const deleteMut = useMutation({
    mutationFn: api.deleteOneCBase,
    onSuccess: (_res, id) => {
      void invalidate();
      if (id === editingId) reset();
      toast.success("База удалена");
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e, "Ошибка удаления базы")),
  });

  const setField = (key: TextField, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (missing.has(key)) {
      setMissing((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const editing = editingId ? data?.bases.find((b) => b.id === editingId) : undefined;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const empty = new Set(REQUIRED.filter((f) => !form[f.key].trim()).map((f) => f.key));
    setMissing(empty);
    if (empty.size > 0) {
      setFormError(
        "Заполните обязательные поля: " +
          REQUIRED.filter((f) => empty.has(f.key))
            .map((f) => f.title)
            .join(", "),
      );
      return;
    }
    setFormError(null);
    const fields = {
      name: form.name.trim(),
      base_url: form.base_url.trim(),
      audience: form.audience.trim(),
    };
    if (editingId) {
      updateMut.mutate({ id: editingId, body: { ...fields, metadata_key: form.metadata_key } });
    } else {
      createMut.mutate({
        ...fields,
        config_type: form.config_type,
        metadata_key: form.metadata_key,
      });
    }
  };

  const startEdit = (b: api.OneCBase) => {
    setEditingId(b.id);
    setForm({
      name: b.name,
      base_url: b.base_url,
      audience: b.audience,
      config_type: b.config_type,
      metadata_key: b.metadata_key,
    });
    setMissing(new Set());
    setFormError(null);
  };

  // Смена конфигурации — ключ пользователя 1С из подходящих ей (первый).
  const setConfigType = (config: api.OneCConfigType) => {
    const keys = api.METADATA_KEYS_BY_CONFIG[config];
    setForm((f) => ({
      ...f,
      config_type: config,
      metadata_key: keys.includes(f.metadata_key) ? f.metadata_key : (keys[0] as api.OneCMetadataKey),
    }));
  };
  const metadataKeys = api.METADATA_KEYS_BY_CONFIG[form.config_type];

  const busy = createMut.isPending || updateMut.isPending;
  const errClass = (k: string) =>
    cn(missing.has(k) && "border-destructive focus-visible:ring-destructive");

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Базы 1С для входа через SSO</h1>
        <p className="text-sm text-muted-foreground">
          При входе через SSO портал ходит в 1С по токену (JWT) под учётной записью человека — в
          базу его подключения, которая должна быть в этом справочнике. Адрес — публикация без{" "}
          <code className="rounded bg-muted px-1">/odata/…</code> и{" "}
          <code className="rounded bg-muted px-1">/hs/…</code>; audience — ровно как в{" "}
          <code className="rounded bg-muted px-1">default.vrd</code> публикации.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {editingId ? `Редактирование: ${editing?.name ?? ""}` : "Новая база"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <div className="space-y-2">
              <Label htmlFor="onec-base-name">Название *</Label>
              <Input
                id="onec-base-name"
                placeholder="БП основная"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className={errClass("name")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="onec-base-url">Адрес публикации *</Label>
              <Input
                id="onec-base-url"
                placeholder="https://1c.mpomega.ru/bp"
                value={form.base_url}
                onChange={(e) => setField("base_url", e.target.value)}
                className={errClass("base_url")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="onec-base-audience">Audience *</Label>
              <Input
                id="onec-base-audience"
                autoComplete="off"
                value={form.audience}
                onChange={(e) => setField("audience", e.target.value)}
                className={errClass("audience")}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="onec-base-config">Конфигурация</Label>
                {/* Конфигурацию заведённой базы сменить нельзя (бэкенд её не меняет). */}
                <select
                  id="onec-base-config"
                  value={form.config_type}
                  disabled={editingId !== null}
                  onChange={(e) => setConfigType(e.target.value as api.OneCConfigType)}
                  className={SELECT_CLASS}
                >
                  {(Object.keys(api.CONFIG_TYPE_TITLES) as api.OneCConfigType[]).map((c) => (
                    <option key={c} value={c}>
                      {api.CONFIG_TYPE_TITLES[c]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="onec-base-metadata">Пользователь 1С из учётки SSO</Label>
                <select
                  id="onec-base-metadata"
                  value={form.metadata_key}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, metadata_key: e.target.value as api.OneCMetadataKey }))
                  }
                  className={SELECT_CLASS}
                >
                  {metadataKeys.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {formError && (
              <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formError}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                {editingId ? "Сохранить" : "Добавить"}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={reset} disabled={busy}>
                  Отмена
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Справочник</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {error && (
          <div className="flex items-center gap-3 text-sm text-destructive">
            Справочник не загрузился: {extractErrorMessage(error, "нет ответа")}
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Повторить
            </Button>
          </div>
        )}
        {data && data.bases.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Баз ещё нет. Пока справочник пуст, в SSO-сессии 1С недоступна.
          </p>
        )}
        <ul className="space-y-2">
          {data?.bases.map((b) => (
            <li
              key={b.id}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium">
                  {b.name}
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {api.CONFIG_TYPE_TITLES[b.config_type] ?? b.config_type}
                  </span>
                </div>
                <div className="truncate text-xs text-muted-foreground">{b.base_url}</div>
                <div className="truncate text-xs text-muted-foreground">
                  aud: {b.audience} · пользователь 1С: {b.metadata_key}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Редактировать ${b.name}`}
                  onClick={() => startEdit(b)}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Удалить ${b.name}`}
                  disabled={deleteMut.isPending}
                  onClick={() => {
                    if (window.confirm(`Удалить базу «${b.name}» из справочника?`)) {
                      deleteMut.mutate(b.id);
                    }
                  }}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
